import { useCallback, useEffect, useState } from 'react';
import {
  type AnalyticsExportColumns,
  type AnalyticsExportFormat,
  type AnalyticsExportJob,
  type AnalyticsExportList,
  type AnalyticsExportRecipient,
  type AnalyticsExportRequest,
  type AnalyticsExportSchedule,
  type AnalyticsExportScheduleInput,
  type AnalyticsExportSource,
  type AnalyticsExportTemplate,
  type AnalyticsExportTemplateFrom,
} from '@vello/api-client';
import { type ExportErrorView, EXPORT_POLL_MS, BI_EXPORT_POLICY, exportErrorView, grantsAllow, needsPolling } from '../../../src/index';
import { useAdmin } from '../../session';
import { downloadBytes } from './download';

/**
 * The asynchronous exports of the operator (D532): the jobs and the files delivered to them, their schedules and their templates, kept on the
 * SERVER (`/v1/analytics/export-*`). A job runs in the worker; while any job is queued or running the list is read again every few seconds (only
 * while the page is visible), and it stops by itself when nothing is open. Nothing is cached across screens: the list is always the server's.
 */
export type ExportLoadable<T> = { readonly status: 'loading' } | { readonly status: 'error'; readonly error: ExportErrorView } | { readonly status: 'ready'; readonly value: T };

export type ExportOutcome<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: ExportErrorView };

const fail = (error: Parameters<typeof exportErrorView>[0]): { ok: false; error: ExportErrorView } => ({ ok: false, error: exportErrorView(error) });

/** The caller holds `analytics:export`: the screens offer to queue, schedule and keep templates. */
export function useCanExport(): boolean {
  const { grants } = useAdmin();
  return grants !== undefined && grantsAllow(grants, BI_EXPORT_POLICY);
}

export interface ExportJobsApi {
  readonly state: ExportLoadable<AnalyticsExportList>;
  readonly reload: () => void;
  readonly enqueue: (input: AnalyticsExportRequest) => Promise<ExportOutcome<AnalyticsExportJob>>;
  /** Cancel an open job or delete a finished one. */
  readonly remove: (id: string) => Promise<ExportOutcome<'CANCELLED' | 'CANCEL_REQUESTED' | 'DELETED'>>;
  /** Fetch the file (the server asks the grants again) and hand it to the browser. */
  readonly download: (id: string) => Promise<ExportOutcome<{ readonly fileName: string }>>;
}

function visible(): boolean {
  try {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
  } catch {
    return true;
  }
}

export function useExportJobs(): ExportJobsApi {
  const { runtime } = useAdmin();
  const api = runtime.analyticsBi;
  const [state, setState] = useState<ExportLoadable<AnalyticsExportList>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const read = useCallback(async (): Promise<AnalyticsExportList | undefined> => {
    const res = await api.exportJobs();
    if (!res.ok) {
      setState((s) => (s.status === 'ready' ? s : { status: 'error', error: exportErrorView(res.error) }));
      return undefined;
    }
    setState({ status: 'ready', value: res.value });
    return res.value;
  }, [api]);

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async (first: boolean): Promise<void> => {
      if (!first && !visible()) {
        timer = setTimeout(() => void tick(false), EXPORT_POLL_MS);
        return;
      }
      const value = await read();
      if (!live) return;
      if (value !== undefined && needsPolling(value.jobs)) timer = setTimeout(() => void tick(false), EXPORT_POLL_MS);
    };
    setState({ status: 'loading' });
    void tick(true);
    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [read, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const enqueue = useCallback<ExportJobsApi['enqueue']>(
    async (input) => {
      const res = await api.enqueueExport(input);
      if (!res.ok) return fail(res.error);
      reload(); // the new job is open: the list reads again and keeps polling
      return { ok: true, value: res.value.job };
    },
    [api, reload],
  );
  const remove = useCallback<ExportJobsApi['remove']>(
    async (id) => {
      const res = await api.removeExport(id);
      // A job that is already gone (deleted in another tab) is gone.
      if (!res.ok && res.error.reasonCode !== 'ANALYTICS_EXPORT_NOT_FOUND') return fail(res.error);
      reload();
      return { ok: true, value: res.ok ? res.value.outcome : 'DELETED' };
    },
    [api, reload],
  );
  const download = useCallback<ExportJobsApi['download']>(
    async (id) => {
      const res = await api.downloadExport(id);
      if (!res.ok) return fail(res.error);
      const saved = downloadBytes(res.value.fileName, res.value.bytes, res.value.contentType);
      return saved ? { ok: true, value: { fileName: res.value.fileName } } : { ok: false, error: { messageId: 'bi.export.blocked', retryable: false, reference: 'DOWNLOAD_BLOCKED' } };
    },
    [api],
  );
  return { state, reload, enqueue, remove, download };
}

// ───────────────────────────── schedules, templates, recipients ─────────────────────────────

export interface ExportSchedulesApi {
  readonly state: ExportLoadable<readonly AnalyticsExportSchedule[]>;
  readonly limit: number;
  readonly reload: () => void;
  readonly save: (input: AnalyticsExportScheduleInput) => Promise<ExportOutcome<AnalyticsExportSchedule>>;
  readonly update: (id: string, patch: Partial<AnalyticsExportScheduleInput>) => Promise<ExportOutcome<AnalyticsExportSchedule>>;
  readonly remove: (id: string) => Promise<ExportOutcome<undefined>>;
}

export function useExportSchedules(enabled = true): ExportSchedulesApi {
  const { runtime } = useAdmin();
  const api = runtime.analyticsBi;
  const [state, setState] = useState<ExportLoadable<readonly AnalyticsExportSchedule[]>>({ status: 'loading' });
  const [limit, setLimit] = useState(20);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setState({ status: 'loading' });
    void api.exportSchedules().then((res) => {
      if (!live) return;
      if (res.ok) {
        setLimit(res.value.limit);
        setState({ status: 'ready', value: res.value.schedules });
      } else setState({ status: 'error', error: exportErrorView(res.error) });
    });
    return () => {
      live = false;
    };
  }, [api, attempt, enabled]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const upsert = useCallback((s: AnalyticsExportSchedule): void => {
    setState((cur) => (cur.status === 'ready' ? { status: 'ready', value: [s, ...cur.value.filter((x) => x.id !== s.id)] } : cur));
  }, []);
  const save = useCallback<ExportSchedulesApi['save']>(
    async (input) => {
      const res = await api.saveExportSchedule(input);
      if (!res.ok) return fail(res.error);
      upsert(res.value.schedule);
      return { ok: true, value: res.value.schedule };
    },
    [api, upsert],
  );
  const update = useCallback<ExportSchedulesApi['update']>(
    async (id, patch) => {
      const res = await api.updateExportSchedule(id, patch);
      if (!res.ok) return fail(res.error);
      setState((cur) => (cur.status === 'ready' ? { status: 'ready', value: cur.value.map((x) => (x.id === id ? res.value.schedule : x)) } : cur));
      return { ok: true, value: res.value.schedule };
    },
    [api],
  );
  const remove = useCallback<ExportSchedulesApi['remove']>(
    async (id) => {
      const res = await api.deleteExportSchedule(id);
      if (!res.ok && res.error.reasonCode !== 'ANALYTICS_EXPORT_NOT_FOUND') return fail(res.error);
      setState((cur) => (cur.status === 'ready' ? { status: 'ready', value: cur.value.filter((x) => x.id !== id) } : cur));
      return { ok: true, value: undefined };
    },
    [api],
  );
  return { state, limit, reload, save, update, remove };
}

export interface ExportTemplatesApi {
  readonly state: ExportLoadable<readonly AnalyticsExportTemplate[]>;
  readonly reload: () => void;
  readonly save: (input: { readonly name: string; readonly from: AnalyticsExportTemplateFrom; readonly columns?: readonly string[]; readonly format?: AnalyticsExportFormat; readonly delimiter?: ',' | ';'; readonly shared?: boolean }) => Promise<ExportOutcome<AnalyticsExportTemplate>>;
  readonly update: (id: string, patch: { readonly name?: string; readonly shared?: boolean }) => Promise<ExportOutcome<AnalyticsExportTemplate>>;
  readonly remove: (id: string) => Promise<ExportOutcome<undefined>>;
}

export function useExportTemplates(enabled = true): ExportTemplatesApi {
  const { runtime } = useAdmin();
  const api = runtime.analyticsBi;
  const [state, setState] = useState<ExportLoadable<readonly AnalyticsExportTemplate[]>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setState({ status: 'loading' });
    void api.exportTemplates().then((res) => {
      if (!live) return;
      setState(res.ok ? { status: 'ready', value: res.value.templates } : { status: 'error', error: exportErrorView(res.error) });
    });
    return () => {
      live = false;
    };
  }, [api, attempt, enabled]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const save = useCallback<ExportTemplatesApi['save']>(
    async (input) => {
      const res = await api.saveExportTemplate(input);
      if (!res.ok) return fail(res.error);
      setState((cur) => (cur.status === 'ready' ? { status: 'ready', value: [res.value.template, ...cur.value] } : cur));
      return { ok: true, value: res.value.template };
    },
    [api],
  );
  const update = useCallback<ExportTemplatesApi['update']>(
    async (id, patch) => {
      const res = await api.updateExportTemplate(id, patch);
      if (!res.ok) return fail(res.error);
      setState((cur) => (cur.status === 'ready' ? { status: 'ready', value: cur.value.map((x) => (x.id === id ? res.value.template : x)) } : cur));
      return { ok: true, value: res.value.template };
    },
    [api],
  );
  const remove = useCallback<ExportTemplatesApi['remove']>(
    async (id) => {
      const res = await api.deleteExportTemplate(id);
      if (!res.ok && res.error.reasonCode !== 'ANALYTICS_EXPORT_NOT_FOUND') return fail(res.error);
      setState((cur) => (cur.status === 'ready' ? { status: 'ready', value: cur.value.filter((x) => x.id !== id) } : cur));
      return { ok: true, value: undefined };
    },
    [api],
  );
  return { state, reload, save, update, remove };
}

/** The operators a schedule may deliver to (loaded when the dialog opens). */
export function useExportRecipients(enabled: boolean): ExportLoadable<readonly AnalyticsExportRecipient[]> {
  const { runtime } = useAdmin();
  const [state, setState] = useState<ExportLoadable<readonly AnalyticsExportRecipient[]>>({ status: 'loading' });
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setState({ status: 'loading' });
    void runtime.analyticsBi.exportRecipients().then((res) => {
      if (!live) return;
      setState(res.ok ? { status: 'ready', value: res.value.recipients } : { status: 'error', error: exportErrorView(res.error) });
    });
    return () => {
      live = false;
    };
  }, [runtime, enabled]);
  return state;
}

/** The columns an export of a source (or a template) would have, from the server's plan (loaded when the dialog opens). */
export function useExportColumns(input: { readonly source?: AnalyticsExportSource; readonly templateId?: string } | undefined): ExportLoadable<AnalyticsExportColumns> {
  const { runtime } = useAdmin();
  const [state, setState] = useState<ExportLoadable<AnalyticsExportColumns>>({ status: 'loading' });
  const key = JSON.stringify(input ?? null);
  useEffect(() => {
    if (input === undefined) return undefined;
    let live = true;
    setState({ status: 'loading' });
    void runtime.analyticsBi.exportColumns(input).then((res) => {
      if (!live) return;
      setState(res.ok ? { status: 'ready', value: res.value } : { status: 'error', error: exportErrorView(res.error) });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` carries the input
  }, [runtime, key]);
  return state;
}
